import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { formatEUR, type TaxableEvent, type TaxSummary } from '../../services/frenchTaxCalculator';

interface TaxDashboardProps {
  events: TaxableEvent[];
  year: number;
  taxSummary: TaxSummary | null;
  onCalculate: () => void;
}

export function TaxDashboard({ events, year, taxSummary, onCalculate }: TaxDashboardProps) {
  const { t } = useTranslation();

  // Calculate quick stats from events
  const stats = useMemo(() => {
    const purchases = events.filter(e => e.type === 'purchase');
    const sales = events.filter(e => e.type === 'sale');
    const rewards = events.filter(e => ['staking_reward', 'mining', 'airdrop'].includes(e.type));

    return {
      totalPurchases: purchases.reduce((sum, e) => sum + e.totalValue, 0),
      totalSales: sales.reduce((sum, e) => sum + e.totalValue, 0),
      totalRewards: rewards.reduce((sum, e) => sum + e.totalValue, 0),
      purchaseCount: purchases.length,
      saleCount: sales.length,
      rewardCount: rewards.length,
    };
  }, [events]);

  // Group events by exchange
  const byExchange = useMemo(() => {
    const grouped: Record<string, number> = {};
    for (const event of events) {
      const exchange = event.exchange || 'Unknown';
      grouped[exchange] = (grouped[exchange] || 0) + 1;
    }
    return Object.entries(grouped).sort((a, b) => b[1] - a[1]);
  }, [events]);

  // Group events by month
  const byMonth = useMemo(() => {
    const grouped: Record<string, { purchases: number; sales: number }> = {};
    for (const event of events) {
      const month = event.date.substring(0, 7); // YYYY-MM
      if (!grouped[month]) {
        grouped[month] = { purchases: 0, sales: 0 };
      }
      if (event.type === 'purchase') {
        grouped[month].purchases += event.totalValue;
      } else if (event.type === 'sale') {
        grouped[month].sales += event.totalValue;
      }
    }
    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
  }, [events]);

  return (
    <div>
      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* Total Purchases */}
        <div style={{
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icons.TrendingUp size={18} style={{ color: '#10b981' }} />
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              {t('taxCenter.dashboard.purchases', 'Purchases')}
            </span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
            {formatEUR(stats.totalPurchases)}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {stats.purchaseCount} {t('taxCenter.dashboard.transactions', 'transactions')}
          </div>
        </div>

        {/* Total Sales */}
        <div style={{
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icons.TrendingDown size={18} style={{ color: '#ef4444' }} />
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              {t('taxCenter.dashboard.sales', 'Sales')}
            </span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#ef4444' }}>
            {formatEUR(stats.totalSales)}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {stats.saleCount} {t('taxCenter.dashboard.transactions', 'transactions')}
          </div>
        </div>

        {/* Rewards */}
        <div style={{
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icons.Gift size={18} style={{ color: '#8b5cf6' }} />
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              {t('taxCenter.dashboard.rewards', 'Rewards')}
            </span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#8b5cf6' }}>
            {formatEUR(stats.totalRewards)}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {stats.rewardCount} {t('taxCenter.dashboard.rewards', 'rewards')}
          </div>
        </div>

        {/* Tax Estimate */}
        <div style={{
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icons.Calculator size={18} style={{ color: '#3b82f6' }} />
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              {t('taxCenter.dashboard.estimatedTax', 'Estimated Tax')}
            </span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6' }}>
            {taxSummary ? formatEUR(taxSummary.flatTax) : '—'}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {taxSummary?.isExempt ? t('taxCenter.dashboard.exempt', 'Exempt (< 305€)') : 'PFU 30%'}
          </div>
        </div>
      </div>

      {/* Tax Summary Card */}
      {taxSummary ? (
        <div style={{
          padding: '24px',
          background: 'var(--hover)',
          borderRadius: '16px',
          marginBottom: '24px',
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: 'var(--text)' }}>
            {t('taxCenter.dashboard.taxSummary', 'Tax Summary')} {year}
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
          }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('taxCenter.dashboard.netGain', 'Net Capital Gain')}
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 700,
                color: taxSummary.netCapitalGain >= 0 ? '#10b981' : '#ef4444',
              }}>
                {formatEUR(taxSummary.netCapitalGain)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('taxCenter.dashboard.taxableAmount', 'Taxable Amount')}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
                {formatEUR(taxSummary.taxableAmount)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('taxCenter.dashboard.incomeTax', 'Income Tax (12.8%)')}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
                {formatEUR(taxSummary.incomeTaxPortion)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('taxCenter.dashboard.socialTax', 'Social Contributions (17.2%)')}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
                {formatEUR(taxSummary.socialContributions)}
              </div>
            </div>
          </div>

          {taxSummary.isExempt && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Icons.Check size={18} style={{ color: '#10b981' }} />
              <span style={{ fontSize: '14px', color: '#10b981' }}>
                {t('taxCenter.dashboard.exemptMessage', 'Your total sales are below €305. No tax due!')}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div style={{
          padding: '48px',
          background: 'var(--hover)',
          borderRadius: '16px',
          marginBottom: '24px',
          textAlign: 'center',
        }}>
          <Icons.Calculator size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--text)' }}>
            {t('taxCenter.dashboard.noCalculation', 'No Tax Calculation Yet')}
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px' }}>
            {t('taxCenter.dashboard.clickCalculate', 'Click the button above to calculate your taxes for')} {year}
          </p>
          <button onClick={onCalculate} className="btn-primary" style={{ padding: '12px 24px' }}>
            <Icons.Calculator size={18} style={{ marginRight: '8px' }} />
            {t('taxCenter.calculate', 'Calculate Taxes')}
          </button>
        </div>
      )}

      {/* Activity by Exchange */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
      }}>
        <div style={{
          padding: '20px',
          background: 'var(--hover)',
          borderRadius: '12px',
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--text)' }}>
            {t('taxCenter.dashboard.byExchange', 'Transactions by Exchange')}
          </h4>
          {byExchange.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {byExchange.slice(0, 5).map(([exchange, count]) => (
                <div key={exchange} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: 'var(--text)' }}>{exchange}</span>
                  <span style={{
                    padding: '4px 10px',
                    background: 'var(--accent)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
              {t('taxCenter.dashboard.noData', 'No transaction data')}
            </p>
          )}
        </div>

        {/* Monthly Activity */}
        <div style={{
          padding: '20px',
          background: 'var(--hover)',
          borderRadius: '12px',
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: 'var(--text)' }}>
            {t('taxCenter.dashboard.monthlyActivity', 'Monthly Activity')}
          </h4>
          {byMonth.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {byMonth.slice(-5).map(([month, data]) => (
                <div key={month} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                    {new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#10b981' }}>
                      +{formatEUR(data.purchases)}
                    </span>
                    <span style={{ fontSize: '12px', color: '#ef4444' }}>
                      -{formatEUR(data.sales)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
              {t('taxCenter.dashboard.noData', 'No transaction data')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
